import {
  Box,
  Typography,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  LinearProgress,
  ListItemIcon,
  CircularProgress,
  Button,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { commonContainerWrapper } from "../Common/styles/flex";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import { DateTime } from "luxon";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { useParams } from "react-router-dom";
import NoteDetailsModal from "../ParticipantRecords/ParticipantNotes/NoteDetailsModal";
import { AxiosResponse } from "axios";
import { Add, MoreVert } from "@mui/icons-material";
import AddGlobalNoteModal from "../ParticipantRecords/ParticipantNotes/AddGlobalNoteModal";

const GlobalNotesList = ({ canEdit }: { canEdit: boolean }) => {
  const { participantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showNoteDetailsModal, setShowNoteDetailsModal] = useState(false);
  const [selectedNoteForDetails, setSelectedNoteForDetails] =
    useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [menuLoader, setMenuLoader] = useState<string>("");
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setLoading(true);
        const res = await http.get(`/participant-notes/${participantId}`);
        const newData = res.data?.data?.map((item: any) => ({
          id: item.id,
          addedBy: item.createdByUser
            ? `${item.createdByUser.firstName || ""} ${
                item.createdByUser.lastName || ""
              }`.trim()
            : "",
          date: item.createdAt
            ? DateTime.fromISO(item.createdAt).toFormat("dd-MM-yyyy hh:mm a")
            : "",
          visibility: item.isPublic ? "global" : "registry",
          note: item.content,
          studyId: item.studyId,
          editable: item.studyId === null,
          studyName: item?.study?.name,
        }));
        setNotes(newData);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        errorToastMessage(error as Error);
      }
    };
    fetchNotes();
  }, [toggleLoader, participantId]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openModal = () => {
    setShowModal(true);
    setAnchorEl(null);
  };

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const truncateText = (text: string, maxLength: number = 120) => {
    return text.substring(0, maxLength) + "...";
  };

  const shouldShowReadMore = (text: string) => {
    if (!text) return false;
    return text.length > 120;
  };

  const handleShowNoteDetails = (note: any) => {
    setSelectedNoteForDetails(note);
    setShowNoteDetailsModal(true);
  };

  const handleCloseNoteDetailsModal = () => {
    setShowNoteDetailsModal(false);
    setSelectedNoteForDetails(null);
  };

  const handleDeleteNote = async () => {
    try {
      setMenuLoader("delete");
      const res: AxiosResponse = await http.delete(
        `/participant-notes/registry/${selectedRow.id}?participantId=${participantId}`
      );
      toastMessage("success", res.data.message);
      setMenuLoader("");
      handleMenuClose();
      refreshPage();
    } catch (err) {
      setMenuLoader("");
      errorToastMessage(err as Error);
    }
  };

  return (
    <Box sx={{ ...commonContainerWrapper, mt: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h2" color="text.primary">
          Notes
        </Typography>
        <Box sx={{ ml: "auto" }}>
          {canEdit && (
            <Button variant="contained" startIcon={<Add />} onClick={openModal}>
              Add Note
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "10px", mt: 3 }}>
        <Box>
          <Table sx={TableBorderRadiusTopLeftRight}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Added By</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell>Visibility</StyledTableCell>
                <StyledTableCell>Study Name</StyledTableCell>
                <StyledTableCell>Note</StyledTableCell>
                <StyledTableCell />
              </TableRow>
            </TableHead>
            {!loading && notes?.length > 0 && (
              <TableBody>
                {notes?.map((row: any) => (
                  <TableRow key={row?.id}>
                    <StyledTableCell>{row?.addedBy || "-"}</StyledTableCell>
                    <StyledTableCell>{row?.date || "-"}</StyledTableCell>
                    <StyledTableCell sx={{ textTransform: "capitalize" }}>
                      {row?.visibility}
                    </StyledTableCell>
                    <StyledTableCell>{row?.studyName || "-"}</StyledTableCell>
                    <StyledTableCell sx={{ maxWidth: "400px" }}>
                      <Typography
                        variant="body1"
                        fontWeight={"regular"}
                        color="text.primary"
                      >
                        {shouldShowReadMore(row?.note)
                          ? truncateText(row?.note)
                          : row?.note || "-"}
                        {shouldShowReadMore(row?.note) && (
                          <Typography
                            component="span"
                            variant="body1"
                            onClick={() => handleShowNoteDetails(row)}
                            sx={{
                              color: "primary.main",
                              fontWeight: "regular",
                              cursor: "pointer",
                              ml: 0.5,
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Read more
                          </Typography>
                        )}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {row?.editable && canEdit && (
                        <IconButton onClick={(e) => handleMenuClick(e, row)}>
                          <MoreVert />
                        </IconButton>
                      )}
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {!loading && notes?.length === 0 && (
            <NoDataContainer>
              <Typography variant="body1" color="gray">
                No Data
              </Typography>
            </NoDataContainer>
          )}
          {loading && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            PaperProps={{
              style: {
                maxHeight: "400px",
                width: "20ch",
              },
            }}
          >
            <MenuItem onClick={openModal} disabled={!!menuLoader}>
              <ListItemText>Edit Note</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeleteNote} disabled={!!menuLoader}>
              <ListItemText>Delete Note</ListItemText>
              {menuLoader === "delete" && (
                <ListItemIcon>
                  <CircularProgress size={18} sx={{ ml: 1 }} />
                </ListItemIcon>
              )}
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      {showModal && (
        <AddGlobalNoteModal
          showModal={showModal}
          closeModal={handleCloseModal}
          refreshPage={refreshPage}
          editData={selectedRow}
        />
      )}

      {showNoteDetailsModal && selectedNoteForDetails && (
        <NoteDetailsModal
          open={showNoteDetailsModal}
          onClose={handleCloseNoteDetailsModal}
          note={selectedNoteForDetails}
        />
      )}
    </Box>
  );
};

export default GlobalNotesList;
